import cx from 'classnames'
import React from 'react'
import Media from 'react-media'

import { Search3D } from '@cinch/oxd-components/icons/src'
import Link, { Appearance } from '@cinch/oxd-components/link/src'
import RecommendedCard from '@cinch/oxd-components/recommended-card/src'
import RecommendedHero from '@cinch/oxd-components/recommended-hero/src'
import SVG from '@cinch/oxd-components/svg/src'
import { Text, TextStyles } from '@cinch-labs/design-system'
import Layout from '@cinch/oxd-layouts/regular/src'
import Statuses from '@cinch/oxd-utils/request-status/src'
import tokens from '@cinch/oxd-utils/tokens/src'

import LoadingPanel from './components/loading-panel'
import LifestyleRecommendations from './hooks/recommendations.hook'

import * as styles from './recommended-cars.module.css'

export interface RecommendedCarsProps {
  i18n: Cinch.WebAppTranslations['recommendations']
  initialValues: any
  onClick: any
}

const PageHeader = ({ title, subTitle, updateAnswer }: { title: string; subTitle?: string; updateAnswer?: string }) => (
  <div className={styles.pageHeader}>
    <div>
      <Text oxford={true} element="h1" className={styles.pageTitle} type={TextStyles.mainTitle}>
        {title}
      </Text>
      {subTitle && <Text oxford={true}>{subTitle}</Text>}
    </div>
    {updateAnswer && (
      <Media defaultMatches={true} query={tokens.customMedia.s}>
        {(matches) =>
          matches && (
            <Link
              oxford={true}
              appearance={Appearance.tertiaryButton}
              to="/lifestyle/budget"
              className={styles.updateAnswersAction}
            >
              {updateAnswer}
            </Link>
          )
        }
      </Media>
    )}
  </div>
)

const NoResultsPageHeader: React.FC<{ title: string }> = ({ title }) => (
  <Text oxford={true} element="h1" className={cx(styles.pageTitle, styles.noResultsTitle)} type={TextStyles.mainTitle}>
    {title}
  </Text>
)

const Page: React.FC<RecommendedCarsProps> = ({ i18n, initialValues, onClick }) => {
  const { recommendations, status } = LifestyleRecommendations(initialValues)
  const { title, listHeading, saleCTA, starMessage, subTitle, updateAnswer, noResults } = i18n
  const isPending = status === Statuses.PENDING
  const noResultsFound = status === Statuses.SUCCESS && !recommendations.length

  const getBody = () => {
    if (isPending) {
      return <LoadingPanel i18n={{ title: i18n.loadingTitle }} />
    }

    if (noResultsFound) {
      return (
        <>
          <NoResultsPageHeader title={noResults.title} />
          <div data-testid="recommendation" className={styles.recommendationHero}>
            <SVG children={Search3D} size={196} className={styles.icon} />
          </div>
          <Text oxford={true} element="h3" className={cx(styles.listingHeader, styles.NoResultsPageHeader)}>
            {noResults.subTitle}
          </Text>
          <div className={styles.noResultsCTAWrapper}>
            <Link
              oxford={true}
              appearance={Appearance.primaryButton}
              className={styles.noResultsCTA}
              to="/lifestyle/budget"
            >
              {noResults.updateAnswer}
            </Link>
            <Link
              oxford={true}
              appearance={Appearance.primaryButton}
              className={styles.noResultsCTA}
              to="/find-vehicle"
            >
              {noResults.saleCTA}
            </Link>
          </div>
        </>
      )
    }

    return (
      <>
        <PageHeader title={title} subTitle={subTitle} updateAnswer={updateAnswer} />
        <div data-testid="recommendation" className={styles.recommendationHero}>
          <>
            {recommendations[0] && (
              <RecommendedHero
                vehicleDetails={recommendations[0]}
                isPending={isPending}
                starMessage={starMessage}
                buttonText={saleCTA}
                onClick={onClick}
              />
            )}
            <Media defaultMatches={true} query={`(max-width: ${tokens.customProperties.breakpoint.xs.max}px)`}>
              {(matches) =>
                matches && (
                  <Link
                    oxford={true}
                    appearance={Appearance.tertiaryButton}
                    to="/lifestyle/budget"
                    className={styles.updateAnswersAction}
                  >
                    {updateAnswer}
                  </Link>
                )
              }
            </Media>
          </>
        </div>
        <Text oxford={true} element="h3" className={styles.listingHeader}>
          {listHeading}
        </Text>
        {recommendations && (
          <div className={styles.recommendationList}>
            {recommendations.map(
              (vehicleDetails, index) =>
                index < 10 &&
                index > 0 && (
                  <RecommendedCard
                    key={index + 1}
                    vehicleDetails={vehicleDetails}
                    isPending={isPending}
                    buttonText={saleCTA}
                    onClick={onClick}
                  />
                ),
            )}
          </div>
        )}
      </>
    )
  }

  return <Layout isFullHeightHero={true} hero={getBody()} />
}

export default Page
